// src/App.tsx - Updated with Login Integration
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "./App.css";
import { Users, ArrowLeft, BarChart3, Stethoscope, LogOut } from "lucide-react";

// Theme Provider
import { ThemeProvider } from "./contexts/ThemeContext";
import { useTheme } from "./contexts/ThemeContext";

// Components
import Login from "./components/Login";
import ResetPassword from "./components/ResetPassword";
import WelcomePage from "./components/WelcomePage";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import Sidebar from "./components/Sidebar";
import PatientCard from "./components/PatientCard";
import AudioControls from "./components/AudioControls";
import TranscriptionDisplay from "./components/TranscriptionDisplay";
import AnalysisSection from "./components/AnalysisSection";
import PatientActivityPanel from "./components/PatientActivityPanel";
import PreviousHandoff from "./components/PreviousHandoff";
import KeyFocusAreas from "./components/KeyFocusAreas";
import LogoutSummary from "./components/LogoutSummary";
import RecordingScreen from "./components/RecordingScreen";
import TooltipSelect from "./components/TooltipSelect";
import VerifyEmail from "./components/VerifyEmail";
import PatientReport from "./components/PatientReport";
import SessionTimeout from "./components/SessionTimeout";
import LogoutWarningModal from "./components/LogoutWarningModal";
import ReportUpdateToast, { ToastMessage } from "./components/ReportUpdateToast";
import FeedbackWidget from "./components/FeedbackWidget";
import PreviousRecordings from "./components/PreviousRecordings";

// Utils
import { parseActivityForReportUpdates, applyUpdatesToReport } from "./utils/activityReportSync";

// Types
import { 
  Patient, 
  SamplePatientsResponse, 
  LookupPatientResponse, 
  TranscriptionData, 
  AnalysisResponse 
} from "./types";

// ---- Constants ----
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";


// ---- Main App Component ----
const AppContent: React.FC = () => {
  const { theme } = useTheme();
  
  // ADD THIS: Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasStartedShift, setHasStartedShift] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<{ first_name: string; last_name: string; email: string; is_nurse_admin?: boolean } | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showNurseAdminLanding, setShowNurseAdminLanding] = useState(false);
  const [showLogoutSummary, setShowLogoutSummary] = useState(false);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [logoutSummaryData, setLogoutSummaryData] = useState<{
    userName: string;
    activitiesLogged: number;
    handoffsSubmitted: number;
    patientsCaresFor: number;
    sessionDuration: string;
    reportsSubmitted: number;
    otherRecordingsSubmitted: number;
    isAdmin?: boolean;
    isNurseAdminDashboard?: boolean;
    isSessionTimeout?: boolean;
  } | null>(null);
  
  // Admin email for analytics access
  const ADMIN_EMAIL = 'admin@careshiftai.com';
  
  // Sidebar toggle state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth <= 480);
  
  // Session state
  const [nurseName, setNurseName] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [patientMRN, setPatientMRN] = useState("");
  const [patientData, setPatientData] = useState<Patient | null>(null);
  const [samplePatients, setSamplePatients] = useState<Patient[]>([]);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordStartTime, setRecordStartTime] = useState<number | null>(null);
  const [transcriptionData, setTranscriptionData] = useState<TranscriptionData | null>(null);
  const [analysis, setAnalysis] = useState("");
  const [audioProcessed, setAudioProcessed] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState("");
  const [claudeApiKey, setClaudeApiKey] = useState<string>("");
  const [statusMsg, setStatusMsg] = useState<string>("");
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [shiftEnded, setShiftEnded] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(true);
  
  // Handoff tracking state
  const [handoffType, setHandoffType] = useState<string>('');
  const [originalLanguage, setOriginalLanguage] = useState<string>('');
  
  // Key Focus Areas cache (persists across tab switches)
  const [focusAreasCache, setFocusAreasCache] = useState<Record<string, { focusAreas: string; hasHandoff: boolean }>>({});
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'report' | 'handoffs' | 'other'>('handoffs');
  
  // Patient Report state (persisted per patient MRN)
  const [patientReports, setPatientReports] = useState<Record<string, Record<string, unknown>>>({});
  
  // Toast notifications for report field updates
  const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]);
  
  // Dynamic highlights state
  const [dynamicHighlights, setDynamicHighlights] = useState<{
    'critical-info': string[];
    'vital-sign': string[];
    'high-priority': string[];
    'medication-highlight': string[];
  } | null>(null);
  
  // Record Other tab state
  const [selectedInteractionType, setSelectedInteractionType] = useState<string>('');
  const [showOtherRecordingScreen, setShowOtherRecordingScreen] = useState(false);
  const [otherAudioBlob, setOtherAudioBlob] = useState<Blob | null>(null);
  const [otherMediaRecorder, setOtherMediaRecorder] = useState<MediaRecorder | null>(null);
  const [isOtherRecording, setIsOtherRecording] = useState(false);
  const [otherTranscriptionData, setOtherTranscriptionData] = useState<TranscriptionData | null>(null);
  const [isOtherTranscribing, setIsOtherTranscribing] = useState(false);
  const [otherAnalysis, setOtherAnalysis] = useState<string>('');
  const [otherAnalysisSummary, setOtherAnalysisSummary] = useState<string>('');
  const [isOtherAnalyzing, setIsOtherAnalyzing] = useState(false);
  const [otherSelectedLanguage, setOtherSelectedLanguage] = useState<string>('English');
  const [otherOriginalAnalysis, setOtherOriginalAnalysis] = useState<string>('');
  const [isOtherTranslating, setIsOtherTranslating] = useState(false);
  const [isEditingOtherSummary, setIsEditingOtherSummary] = useState(false);
  const [editedOtherSummary, setEditedOtherSummary] = useState<string>('');
  const [isSubmittingOther, setIsSubmittingOther] = useState(false);
  const [otherSubmitStatus, setOtherSubmitStatus] = useState<string>('');
  const [otherKeyPoints, setOtherKeyPoints] = useState<string[]>([]);
  const [otherCategoricalBreakdown, setOtherCategoricalBreakdown] = useState<Record<string, string>>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    summary: false,
    keyPoints: false,
    categorical: false
  });
  const [analysisStep, setAnalysisStep] = useState<string>('');
  
  // Shift tracking state - persist across refreshes
  const [shiftStartTime, setShiftStartTime] = useState<Date>(() => {
    const saved = localStorage.getItem('shiftStartTime');
    return saved ? new Date(saved) : new Date();
  });
  const [shiftDuration, setShiftDuration] = useState<number>(() => {
    const saved = localStorage.getItem('shiftDuration');
    return saved ? parseFloat(saved) : 12; // Default 12 hours
  });
  const [nurseStats, setNurseStats] = useState({
    patientsCount: 0,
    activitiesEntered: 0,
    handoffsCompleted: 0,
    totalTimeInApp: 0,
    reportsSubmitted: 0,
    otherRecordingsSubmitted: 0
  });
  
  // Update nurse stats when patients change
  useEffect(() => {
    setNurseStats(prev => ({
      ...prev,
      patientsCount: samplePatients.length
    }));
  }, [samplePatients]);

  // ADD THIS: Handle successful login
  const handleLoginSuccess = useCallback(() => {
    setIsAuthenticated(true);
    // Persist authentication state to localStorage
    localStorage.setItem('isAuthenticated', 'true');
    // Load user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setLoggedInUser(user);
      // Check if user is a nurse admin - show landing screen to choose between admin dashboard and nurse view
      if (user.is_nurse_admin) {
        setShowNurseAdminLanding(true);
      }
    }
  }, []);

  // ADD THIS: Handle starting shift
  const handleStartShift = useCallback((shiftEndTime?: Date) => {
    setHasStartedShift(true);
    // Reset shift start time to now when starting a new shift
    const newShiftStart = new Date();
    setShiftStartTime(newShiftStart);
    // Persist to localStorage so it survives page refreshes
    localStorage.setItem('shiftStartTime', newShiftStart.toISOString());
    
    // Calculate and set shift duration based on end time
    if (shiftEndTime) {
      const durationMs = shiftEndTime.getTime() - newShiftStart.getTime();
      const durationHours = durationMs / (1000 * 60 * 60);
      setShiftDuration(durationHours);
      localStorage.setItem('shiftDuration', durationHours.toString());
      localStorage.setItem('shiftEndTime', shiftEndTime.toISOString());
    } else {
      // Default 12 hours for admin or if no end time specified
      setShiftDuration(12);
      localStorage.setItem('shiftDuration', '12');
    }
    
    // Log session start to database (skip for admin accounts)
    const isAdmin = loggedInUser?.email?.toLowerCase() === 'admin@careshiftai.com';
    if (loggedInUser?.email && !isAdmin) {
      axios.post<{ success: boolean; session_id?: string }>(`${BACKEND_URL}/auth/session/start`, {
        email: loggedInUser.email
      })
      .then(res => {
        if (res.data.success && res.data.session_id) {
          // Store session_id in localStorage to use when logging out
          localStorage.setItem('sessionId', res.data.session_id);
          console.log('Session started, ID:', res.data.session_id);
        }
      })
      .catch(err => {
        console.error('Failed to log session start:', err);
      });
    }

  }, [loggedInUser]);

  // ADD THIS: Check if user is already authenticated (optional)
  useEffect(() => {
  // Check if we're preserving auth from a reset
  const preserveAuth = sessionStorage.getItem('preserveAuth');
  
  if (preserveAuth === 'true') {
    // Clear the flag
    sessionStorage.removeItem('preserveAuth');
    // Keep user authenticated
    localStorage.setItem('isAuthenticated', 'true');
    setIsAuthenticated(true);
    setHasStartedShift(true); // Skip welcome page on reset
    // Load user data
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setLoggedInUser(user);
      // If admin, show analytics page
      if (user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setShowAnalytics(true);
      }
    }
  } else {
    // Normal authentication check
    const storedAuth = localStorage.getItem('isAuthenticated');
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
      setHasStartedShift(true); // Skip welcome page if already authenticated
      // Load user data
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setLoggedInUser(user);
        // If admin, show analytics page
        if (user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
          setShowAnalytics(true);
        }
        // If nurse admin, check if they were on the dashboard
        if (user.is_nurse_admin) {
          const wasOnDashboard = localStorage.getItem('nurseAdminOnDashboard');
          if (wasOnDashboard === 'true') {
            setShowAnalytics(true);
            setShowNurseAdminLanding(false);
          }
        }
      }
    }
  }
}, []);

  // ---- Load API key (from .env, or preference) ----
  useEffect(() => {
    setClaudeApiKey(process.env.REACT_APP_CLAUDE_API_KEY || "");
  }, []);

  // ---- Check backend health ----
  useEffect(() => {
    axios.get(`${BACKEND_URL}/health`)
      .then((res) => setBackendAvailable(res.status === 200))
      .catch(() => setBackendAvailable(false));
  }, []);

  // ---- Get patients for the logged-in user ----
  useEffect(() => {
    if (!backendAvailable || !loggedInUser) return;
    
    axios.get<SamplePatientsResponse>(`${BACKEND_URL}/patients/sample?email=${encodeURIComponent(loggedInUser.email)}`)
      .then((res) => setSamplePatients(res.data.success ? res.data.patients : []))
      .catch(() => setSamplePatients([]));
  }, [backendAvailable, loggedInUser]);

  // ---- Clear patient data when patientMRN is cleared (e.g., department change) ----
  useEffect(() => {
    if (!patientMRN) {
      setPatientData(null);
      setStatusMsg('');
      // Reset related states
      setShiftEnded(false);
      setHandoffType('');
      setOriginalLanguage('');
      setAnalysis('');
      setEditedTranscript('');
      setTranscriptionData(null);
      setAudioProcessed(false);
      setProcessing(false);
      setActiveTab('handoffs');
    }
  }, [patientMRN]);

  // ---- Audio Recording ----
  useEffect(() => {
    if (!mediaRecorder) return;
    let chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (e) => { chunks.push(e.data); };
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: "audio/wav" });
      setAudioBlob(blob);
      setAudioProcessed(false);
      setStatusMsg("Audio recorded. Ready for transcription.");
      chunks = [];
    };
  }, [mediaRecorder]);

  // ---- Helpers ---- 
  const resetSession = useCallback(() => {
  // Clear all session state except authentication
  setNurseName("");
  setEmployeeId("");
  setPatientMRN("");
  setPatientData(null);
  setAudioBlob(null);
  setMediaRecorder(null);
  setIsRecording(false);
  setRecordStartTime(null);
  setTranscriptionData(null);
  setAnalysis("");
  setAudioProcessed(false);
  setEditedTranscript("");
  setStatusMsg("");
  setProcessing(false);
  setShiftEnded(false);
  setShowActivityPanel(true);
  // Reset handoff tracking
  setHandoffType('');
  setOriginalLanguage('');
  
  // Reset Nurse Scribe (Record Other) tab state
  setSelectedInteractionType('');
  setOtherAudioBlob(null);
  setOtherMediaRecorder(null);
  setIsOtherRecording(false);
  setOtherTranscriptionData(null);
  setIsOtherTranscribing(false);
  setOtherAnalysis('');
  setOtherAnalysisSummary('');
  setIsOtherAnalyzing(false);
  setOtherSelectedLanguage('English');
  setOtherOriginalAnalysis('');
  setEditedOtherSummary('');
  setOtherSubmitStatus('');
  
  // Preserve authentication data in localStorage
  const auth = localStorage.getItem('isAuthenticated');
  const user = localStorage.getItem('user');
  const hasStarted = localStorage.getItem('hasStartedShift');
  // Don't clear localStorage - just reset the session state
  // This keeps the user logged in
  }, []);

  // Handle handoff submission - increment counter and reset session
  const handleHandoffSubmitted = useCallback(() => {
    // Increment handoffs counter
    setNurseStats(prev => ({
      ...prev,
      handoffsCompleted: prev.handoffsCompleted + 1
    }));
    // Reset session for next patient
    resetSession();
  }, [resetSession]);

  // Calculate session duration as a formatted string
  const getSessionDuration = useCallback(() => {
    const now = new Date();
    const diffMs = now.getTime() - shiftStartTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }, [shiftStartTime]);

  // Complete the logout after summary is shown
  const completeLogout = useCallback(() => {
    // Log session end to database (skip for admin)
    const sessionId = localStorage.getItem('sessionId');
    const userEmail = loggedInUser?.email;
    const userName = loggedInUser ? `${loggedInUser.first_name} ${loggedInUser.last_name}` : '';
    const isAdmin = userEmail?.toLowerCase() === 'admin@careshiftai.com';
    
    if (sessionId && userEmail && !isAdmin) {
      axios.post<{ success: boolean }>(`${BACKEND_URL}/auth/session/end`, {
        email: userEmail,
        session_id: sessionId
      })
      .then(res => {
        if (res.data.success) {
          console.log('Session ended successfully');
        }
        // Clear session ID from localStorage
        localStorage.removeItem('sessionId');
      })
      .catch(err => {
        console.error('Failed to log session end:', err);
        // Clear session ID even on error
        localStorage.removeItem('sessionId');
      });
    }
    
    // Hide logout summary
    setShowLogoutSummary(false);
    setLogoutSummaryData(null);
    
    // Clear all authentication state
    setIsAuthenticated(false);
    setHasStartedShift(false);
    setLoggedInUser(null);
    setShowAnalytics(false);
    setShowNurseAdminLanding(false);
    
    // Clear localStorage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('hasStartedShift');
    localStorage.removeItem('shiftStartTime');
    localStorage.removeItem('shiftDuration');
    localStorage.removeItem('shiftEndTime');
    localStorage.removeItem('nurseAdminOnDashboard');
    
    // Reset shift tracking state
    setShiftStartTime(new Date());
    setShiftDuration(12);
    setNurseStats({
      patientsCount: 0,
      activitiesEntered: 0,
      handoffsCompleted: 0,
      totalTimeInApp: 0,
      reportsSubmitted: 0,
      otherRecordingsSubmitted: 0
    });
    
    // Reset session state
    resetSession();
  }, [resetSession, loggedInUser]);

  // Proceed with actual logout (called after warning or directly if handoff submitted)
  const proceedWithLogout = useCallback(() => {
    const isAdmin = loggedInUser?.email?.toLowerCase() === 'admin@careshiftai.com';
    const isNurseAdminUser = loggedInUser?.is_nurse_admin === true;
    
    // For nurse admin viewing analytics dashboard OR on landing page, show redirect screen (not stats summary)
    if (isNurseAdminUser && (showAnalytics || showNurseAdminLanding)) {
      // Show brief "logging out" message then redirect
      setLogoutSummaryData({
        userName: loggedInUser?.first_name || 'Admin',
        activitiesLogged: 0,
        handoffsSubmitted: 0,
        patientsCaresFor: 0,
        sessionDuration: '',
        reportsSubmitted: 0,
        otherRecordingsSubmitted: 0,
        isAdmin: true,
        isNurseAdminDashboard: true  // Flag to show redirect screen
      });
      setShowLogoutSummary(true);
      return;
    }
    
    // For super admin, show simple logout message
    if (isAdmin) {
      setLogoutSummaryData({
        userName: 'Admin',
        activitiesLogged: 0,
        handoffsSubmitted: 0,
        patientsCaresFor: 0,
        sessionDuration: '',
        reportsSubmitted: 0,
        otherRecordingsSubmitted: 0,
        isAdmin: true
      });
      setShowLogoutSummary(true);
      return;
    }
    
    // Calculate stats for the summary
    const userName = loggedInUser ? loggedInUser.first_name : 'Nurse';
    const sessionDuration = getSessionDuration();
    
    // Set logout summary data
    setLogoutSummaryData({
      userName,
      activitiesLogged: nurseStats.activitiesEntered,
      handoffsSubmitted: nurseStats.handoffsCompleted,
      patientsCaresFor: nurseStats.patientsCount,
      sessionDuration,
      reportsSubmitted: nurseStats.reportsSubmitted,
      otherRecordingsSubmitted: nurseStats.otherRecordingsSubmitted
    });
    
    // Show the logout summary screen
    setShowLogoutSummary(true);
  }, [loggedInUser, nurseStats, showAnalytics, showNurseAdminLanding, getSessionDuration]);

  // Handle logout - check if handoff was submitted first
  const handleLogout = useCallback(() => {
    const isAdmin = loggedInUser?.email?.toLowerCase() === 'admin@careshiftai.com';
    const isNurseAdminUser = loggedInUser?.is_nurse_admin === true;
    
    // Skip warning for admin, nurse admin (when in admin dashboard), or if handoff was already submitted
    // Also skip if user hasn't started a shift yet (e.g., nurse admin on landing/dashboard)
    if (isAdmin || isNurseAdminUser || nurseStats.handoffsCompleted > 0 || !hasStartedShift) {
      proceedWithLogout();
      return;
    }
    
    // Show warning modal if no handoff submitted
    setShowLogoutWarning(true);
  }, [loggedInUser, nurseStats.handoffsCompleted, hasStartedShift, proceedWithLogout]);

  // Handle "Log Out Anyway" from warning modal
  const handleLogoutAnyway = useCallback(() => {
    setShowLogoutWarning(false);
    proceedWithLogout();
  }, [proceedWithLogout]);

  // Handle "Stay Logged In" from warning modal
  const handleStayLoggedIn = useCallback(() => {
    setShowLogoutWarning(false);
  }, []);

  // Handle session timeout logout - show simple redirect screen instead of stats summary
  const handleSessionTimeoutLogout = useCallback(() => {
    setLogoutSummaryData({
      userName: loggedInUser?.first_name || 'User',
      activitiesLogged: 0,
      handoffsSubmitted: 0,
      patientsCaresFor: 0,
      sessionDuration: '',
      reportsSubmitted: 0,
      otherRecordingsSubmitted: 0,
      isSessionTimeout: true
    });
    setShowLogoutSummary(true);
  }, [loggedInUser]);

  // Handle activity saved - sync with report form using LLM
  const handleActivitySavedWithSync = useCallback(async (type: 'text' | 'audio', activityText: string) => {
    // Increment activity counter
    setNurseStats(prev => ({
      ...prev,
      activitiesEntered: prev.activitiesEntered + 1
    }));

    // Use LLM to intelligently parse activity for report field updates
    if (patientData) {
      const department = patientData.Department || 'Med-Surg';
      
      // Use LLM for all supported departments
      const llmSupportedDepartments = ['Med-Surg', 'Rehab', 'Cardiology', 'Labor & Delivery', 'ER', 'ICU', 'NICU', 'PACU', 'Pediatrics', 'Oncology', 'Neurology', 'Psychiatry', 'Women\'s Services (OB)'];
      if (llmSupportedDepartments.includes(department)) {
        try {
          const response = await axios.post<{
            success: boolean;
            updates: Array<{
              section: string;
              field: string;
              value: string;
              fieldLabel: string;
            }>;
            reasoning?: string;
          }>(`${BACKEND_URL}/activity/parse`, {
            activity_text: activityText,
            department: department
          });
          
          if (response.data.success && response.data.updates.length > 0) {
            const currentReport = (patientReports[patientData.MRN] || {}) as Record<string, Record<string, string>>;
            let updatedReport = { ...currentReport };
            const changedFields: Array<{ fieldLabel: string; isAppend: boolean }> = [];
            
            // Apply each update from LLM
            for (const update of response.data.updates) {
              const { section, field, value, fieldLabel } = update;
              
              // Handle otherNotes as a string field, not a nested object
              if (section === 'otherNotes') {
                const existingValue = (updatedReport.otherNotes as unknown as string) || '';
                const isAppend = existingValue.length > 0;
                if (isAppend) {
                  (updatedReport as Record<string, unknown>).otherNotes = `${existingValue}\n${value}`;
                } else {
                  (updatedReport as Record<string, unknown>).otherNotes = value;
                }
                changedFields.push({ fieldLabel: fieldLabel || 'Other Notes', isAppend });
              } else {
                // Ensure section exists
                if (!updatedReport[section]) {
                  updatedReport[section] = {};
                }
                
                const existingValue = updatedReport[section][field] || '';
                const isAppend = existingValue.length > 0;
                
                // Append or set value
                if (isAppend) {
                  updatedReport[section][field] = `${existingValue}, ${value}`;
                } else {
                  updatedReport[section][field] = value;
                }
                
                changedFields.push({ fieldLabel, isAppend });
              }
            }
            
            // Update the report state
            setPatientReports(prev => ({
              ...prev,
              [patientData.MRN]: updatedReport
            }));
            
            // Show toast notifications
            const newToasts: ToastMessage[] = changedFields.map((field, index) => ({
              id: `${Date.now()}-${index}`,
              fieldLabel: field.fieldLabel,
              message: field.isAppend ? `Added to report` : `Updated in report`
            }));
            
            setToastMessages(prev => [...prev, ...newToasts]);
          }
        } catch (error) {
          console.error('LLM activity parsing failed, falling back to pattern matching:', error);
          // Fall back to pattern matching if LLM fails
          const updates = parseActivityForReportUpdates(activityText, department);
          if (updates.length > 0) {
            const currentReport = (patientReports[patientData.MRN] || {}) as Record<string, Record<string, string>>;
            const { updatedReport, changedFields } = applyUpdatesToReport(currentReport, updates);
            if (changedFields.length > 0) {
              setPatientReports(prev => ({ ...prev, [patientData.MRN]: updatedReport }));
              const newToasts: ToastMessage[] = changedFields.map((field, index) => ({
                id: `${Date.now()}-${index}`,
                fieldLabel: field.fieldLabel,
                message: field.isAppend ? `Added to report` : `Updated in report`
              }));
              setToastMessages(prev => [...prev, ...newToasts]);
            }
          }
        }
      } else {
        // Use pattern matching for non-Med-Surg departments
        const updates = parseActivityForReportUpdates(activityText, department);
        if (updates.length > 0) {
          const currentReport = (patientReports[patientData.MRN] || {}) as Record<string, Record<string, string>>;
          const { updatedReport, changedFields } = applyUpdatesToReport(currentReport, updates);
          if (changedFields.length > 0) {
            setPatientReports(prev => ({ ...prev, [patientData.MRN]: updatedReport }));
            const newToasts: ToastMessage[] = changedFields.map((field, index) => ({
              id: `${Date.now()}-${index}`,
              fieldLabel: field.fieldLabel,
              message: field.isAppend ? `Added to report` : `Updated in report`
            }));
            setToastMessages(prev => [...prev, ...newToasts]);
          }
        }
      }
    }
  }, [patientData, patientReports]);

  // Dismiss toast notification
  const dismissToast = useCallback((id: string) => {
    setToastMessages(prev => prev.filter(msg => msg.id !== id));
  }, []);
  
  const lookupPatient = useCallback(async (mrn: string) => {
    try {
      setStatusMsg("Looking up patient...");
      // Include added_by to get user-specific patient data
      const userName = loggedInUser ? `${loggedInUser.first_name} ${loggedInUser.last_name}` : '';
      const res = await axios.get<LookupPatientResponse>(
        `${BACKEND_URL}/patient/lookup/${mrn}?added_by=${encodeURIComponent(userName)}`
      );
      if (res.data.success) {
        setPatientData(res.data.patient);
        setPatientMRN(mrn);
        // Reset shift and handoff states when selecting a new patient
        setShiftEnded(false);
        setHandoffType('');
        setOriginalLanguage('');
        setAnalysis('');
        setEditedTranscript('');
        setTranscriptionData(null);
        setAudioProcessed(false);
        setProcessing(false);
        setActiveTab('handoffs'); // Reset to handoffs tab
        setStatusMsg("");
      } else {
        setPatientData(null);
        setStatusMsg("Patient not found.");
      }
    } catch {
      setStatusMsg("Patient lookup failed.");
    }
  }, [loggedInUser]);

  const transcribeAudio = useCallback(async (blob: Blob) => {
    setProcessing(true);
    setStatusMsg("Transcribing audio...");
    const formData = new FormData();
    formData.append("audio", blob);
    try {
      const res = await axios.post<TranscriptionData>(`${BACKEND_URL}/audio/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.data.success) {
        setTranscriptionData(res.data);
        setAudioProcessed(true);
        // Don't set editedTranscript here - it will be set during analysis typing animation
        // This prevents the raw transcription from showing in the right column
        setStatusMsg("Audio transcription completed and ready for analysis.");
      } else {
        setStatusMsg("Transcription failed. Please try again.");
      }
    } catch (error) {
      console.error("Transcription error:", error);
      setStatusMsg("Audio transcription error. Please check the backend connection.");
    }
    setProcessing(false);
  }, []);

  // Helper function to extract grammatically corrected text from analysis
  const extractCorrectedTranscript = (analysis: string): string => {
    // Look for the first emoji that marks the start of categorized sections
    const emojiPattern = /🫀|🫁|🧠|💧|🔬|💊|⚠️|🚨|⏰|🔍|👨‍⚕️|🏥|😴|📊|🩹|🤢|🍽️|🚶‍♂️|👶|🍼|💉|👪/;
    const match = analysis.match(emojiPattern);
    
    if (match && match.index !== undefined && match.index > 0) {
      // Extract text before the first emoji (this is the corrected transcript)
      let correctedText = analysis.substring(0, match.index).trim();
      // Remove any HTML tags that might be in the corrected text
      correctedText = correctedText.replace(/<[^>]*>/g, '');
      // Remove common transition phrases like "now let's break it down:" or similar
      correctedText = correctedText.replace(/\n*now,?\s*let'?s?\s*break\s*(it|this)?\s*down:?\s*$/i, '').trim();
      correctedText = correctedText.replace(/\n*here'?s?\s*(a\s*)?(quick\s*)?breakdown:?\s*$/i, '').trim();
      correctedText = correctedText.replace(/\n*let me break\s*(it|this)?\s*down:?\s*$/i, '').trim();
      correctedText = correctedText.replace(/\n*alright,?\s*here'?s?\s*(a\s*)?(quick\s*)?breakdown\s*(of\s*the\s*key\s*points)?:?\s*$/i, '').trim();
      correctedText = correctedText.replace(/\n*okay,?\s*here'?s?\s*(a\s*)?(quick\s*)?breakdown:?\s*$/i, '').trim();
      correctedText = correctedText.replace(/\n*here\s*are\s*the\s*key\s*points:?\s*$/i, '').trim();
      return correctedText;
    }
    
    // If no emojis found, return the original analysis without HTML tags
    return analysis.replace(/<[^>]*>/g, '');
  };

  const analyzeWithClaude = useCallback(async (transcript: string, patient: Patient, apiKey: string) => {
    if (!transcript.trim()) {
      setStatusMsg("No transcript available for analysis.");
      return;
    }
    
    setProcessing(true);
    setStatusMsg("Analyzing Your Handoff...");
    try {
      const res = await axios.post<AnalysisResponse>(`${BACKEND_URL}/analysis/claude`, {
        transcript,
        patient_data: patient,
        api_key: apiKey,
      });
      if (res.data.success) {
        const fullAnalysis = res.data.analysis;
        
        // Extract the grammatically corrected transcript (narrative portion)
        const correctedTranscript = extractCorrectedTranscript(fullAnalysis);
        
        // Type out the narrative WITHOUT highlighting (plain text)
        setAnalysis(""); // Start empty - will be set after typing completes
        setEditedTranscript(""); // Will store the narrative during typing
        setStatusMsg("Displaying analysis...");
        
        let currentIndex = 0;
        const typingSpeed = 12; // milliseconds per character
        
        const typeNextChar = () => {
          if (currentIndex < correctedTranscript.length) {
            const currentText = correctedTranscript.slice(0, currentIndex + 1);
            // Apply highlighting to narrative as it types
            const highlightedCurrentText = highlightKeywords(currentText);
            setEditedTranscript(highlightedCurrentText);
            currentIndex++;
            setTimeout(typeNextChar, typingSpeed);
          } else {
            // Typing complete - set the full analysis (Claude already highlighted the categorical breakdown)
            setAnalysis(fullAnalysis);
            // Keep the highlighted narrative for display
            setEditedTranscript(highlightKeywords(correctedTranscript));
            setStatusMsg("Analysis completed successfully.");
            setProcessing(false);
          }
        };
        
        typeNextChar();
        return; // Don't set processing to false here, it's done in typeNextChar
      } else {
        setAnalysis("");
        setStatusMsg("Analysis failed. Please check your Claude API key or try again.");
      }
    } catch (error) {
      console.error("AI analysis error:", error);
      setAnalysis("");
      setStatusMsg("Claude analysis error. Please check the backend connection.");
    }
    setProcessing(false);
  }, []);

  const saveHandoffToCSV = useCallback((handoffData: any) => {
    const csvHeaders = [
      "timestamp",
      "nurse_name",
      "employee_id",
      "patient_mrn",
      "patient_name",
      "department",
      "detected_language",
      "original_transcript",
      "english_transcript",
      "ai_analysis"
    ];
    const row = [
      new Date().toISOString(),
      handoffData.nurse_name,
      handoffData.employee_id,
      handoffData.patient_mrn,
      handoffData.patient_name,
      handoffData.department || "",
      handoffData.detected_language,
      (handoffData.original_transcript || "").replace(/\n/g, " "),
      (handoffData.english_transcript || "").replace(/\n/g, " "),
      (handoffData.ai_analysis || "").replace(/\n/g, " "),
    ];
    const csv = [csvHeaders.join(","), row.map(f => `"${String(f).replace(/"/g, '""')}"`).join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute("download", `handoff_${handoffData.patient_mrn}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setStatusMsg("Handoff saved to CSV.");
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setStatusMsg("Requesting microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      setMediaRecorder(mr);
      setIsRecording(true);
      setRecordStartTime(Date.now());
      mr.start();
      setStatusMsg("Recording in progress...");
      
      setAudioBlob(null);
      setTranscriptionData(null);
      setAnalysis("");
      setEditedTranscript("");
      setAudioProcessed(false);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setStatusMsg("Error accessing microphone. Please check permissions.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setRecordStartTime(null);
      setStatusMsg("Recording stopped. Audio ready for transcription.");
    }
  }, [mediaRecorder, isRecording]);

  // Record Other tab recording functions
  const startOtherRecording = useCallback(async () => {
    try {
      setStatusMsg("Requesting microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setOtherAudioBlob(blob);
        setShowOtherRecordingScreen(false);
        setStatusMsg("Recording stopped. Audio ready for playback.");
      };
      
      setOtherMediaRecorder(mr);
      setIsOtherRecording(true);
      setShowOtherRecordingScreen(true);
      setOtherAudioBlob(null);
      mr.start();
      setStatusMsg("Recording in progress...");
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setStatusMsg("Error accessing microphone. Please check permissions.");
    }
  }, []);

  const stopOtherRecording = useCallback(() => {
    if (otherMediaRecorder && isOtherRecording) {
      otherMediaRecorder.stop();
      otherMediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsOtherRecording(false);
    }
  }, [otherMediaRecorder, isOtherRecording]);

  // Reset Nurse Scribe tab for re-recording (keeps interaction type selected)
  const resetNurseScribeForRerecord = useCallback(() => {
    setOtherAudioBlob(null);
    setOtherTranscriptionData(null);
    setOtherAnalysis('');
    setOtherAnalysisSummary('');
    setOtherOriginalAnalysis('');
    setEditedOtherSummary('');
    setIsEditingOtherSummary(false);
    setStatusMsg("Ready to record a new audio.");
  }, []);

  // Cancel and clear all Nurse Scribe tab content
  const cancelNurseScribe = useCallback(() => {
    setSelectedInteractionType('');
    setOtherAudioBlob(null);
    setOtherMediaRecorder(null);
    setIsOtherRecording(false);
    setOtherTranscriptionData(null);
    setOtherAnalysis('');
    setOtherAnalysisSummary('');
    setOtherOriginalAnalysis('');
    setEditedOtherSummary('');
    setIsEditingOtherSummary(false);
    setShowOtherRecordingScreen(false);
    setStatusMsg("Nurse Scribe cleared.");
  }, []);

  const transcribeOtherAudio = useCallback(async (blob: Blob) => {
    setIsOtherTranscribing(true);
    setStatusMsg("Transcribing audio with speaker detection...");
    const formData = new FormData();
    formData.append("audio", blob);
    try {
      // Try diarized transcription first (with speaker detection)
      const res = await axios.post<TranscriptionData & { speaker_count?: number }>(`${BACKEND_URL}/audio/transcribe-diarized`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.data.success) {
        setOtherTranscriptionData(res.data);
        const speakerCount = res.data.speaker_count || 1;
        setStatusMsg(`Transcription completed. Detected ${speakerCount} speaker${speakerCount > 1 ? 's' : ''}.`);
      } else {
        // Fallback to regular transcription if diarization fails
        setStatusMsg("Speaker detection unavailable, using standard transcription...");
        const fallbackRes = await axios.post<TranscriptionData>(`${BACKEND_URL}/audio/transcribe`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        if (fallbackRes.data.success) {
          setOtherTranscriptionData(fallbackRes.data);
          setStatusMsg("Audio transcription completed (without speaker detection).");
        } else {
          setStatusMsg("Transcription failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Diarized transcription error, trying fallback:", error);
      // Fallback to regular transcription
      try {
        const fallbackRes = await axios.post<TranscriptionData>(`${BACKEND_URL}/audio/transcribe`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        if (fallbackRes.data.success) {
          setOtherTranscriptionData(fallbackRes.data);
          setStatusMsg("Audio transcription completed (speaker detection unavailable).");
        } else {
          setStatusMsg("Transcription failed. Please try again.");
        }
      } catch (fallbackError) {
        console.error("Fallback transcription error:", fallbackError);
        setStatusMsg("Audio transcription error. Please check the backend connection.");
      }
    }
    setIsOtherTranscribing(false);
  }, []);

  const analyzeOtherRecording = useCallback(async () => {
    if (!otherTranscriptionData) return;
    
    const transcript = otherTranscriptionData.translation || otherTranscriptionData.transcription;
    if (!transcript) return;
    
    // Auto-scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setIsOtherAnalyzing(true);
    setStatusMsg("Analyzing recording...");
    setAnalysisStep('Summarizing transcript...');
    setOtherAnalysis('');
    setOtherAnalysisSummary('');
    setOtherKeyPoints([]);
    setOtherCategoricalBreakdown({});
    
    try {
      // Simulate step progression with timeouts (backend does all at once, but we show progress)
      const stepTimeout1 = setTimeout(() => setAnalysisStep('Extracting key points...'), 2000);
      const stepTimeout2 = setTimeout(() => setAnalysisStep('Categorizing information...'), 4000);
      
      const response = await axios.post<{ 
        success: boolean; 
        summary?: string;
        key_points?: string[];
        categorical_breakdown?: Record<string, string>;
      }>(`${BACKEND_URL}/analysis/interaction`, {
        transcript,
        interaction_type: selectedInteractionType,
        patient_data: patientData,
        department: patientData?.Department || 'Med-Surg'
      });
      
      // Clear timeouts if response comes back quickly
      clearTimeout(stepTimeout1);
      clearTimeout(stepTimeout2);
      
      if (response.data.success && response.data.summary) {
        const summaryText = response.data.summary;
        const keyPoints = response.data.key_points || [];
        const categoricalBreakdown = response.data.categorical_breakdown || {};
        
        // Store original for translation purposes
        setOtherOriginalAnalysis(summaryText);
        setOtherKeyPoints(keyPoints);
        setOtherCategoricalBreakdown(categoricalBreakdown);
        
        // Strip HTML highlights for typing animation, will add back after typing completes
        const stripHighlights = (text: string) => text.replace(/<span[^>]*>([^<]*)<\/span>/g, '$1');
        const textWithoutHighlights = stripHighlights(summaryText);
        
        // Type out the summary without highlights
        let currentIndex = 0;
        const typingSpeed = 12;
        setAnalysisStep('');
        
        const typeNextChar = () => {
          if (currentIndex < textWithoutHighlights.length) {
            const currentText = textWithoutHighlights.slice(0, currentIndex + 1);
            setOtherAnalysisSummary(currentText);
            currentIndex++;
            setTimeout(typeNextChar, typingSpeed);
          } else {
            // Typing complete - now apply the summary WITH highlights
            setOtherAnalysis(summaryText);
            setOtherAnalysisSummary(summaryText);
            setEditedOtherSummary(summaryText);
            setStatusMsg("Analysis completed successfully.");
            setIsOtherAnalyzing(false);
          }
        };
        
        typeNextChar();
        return;
      } else {
        setStatusMsg("Analysis failed. Please try again.");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      setStatusMsg("Analysis error. Please check the backend connection.");
    }
    setAnalysisStep('');
    setIsOtherAnalyzing(false);
  }, [otherTranscriptionData, selectedInteractionType, patientData]);

  const translateOtherAnalysis = useCallback(async (targetLanguage: string) => {
    if (!otherOriginalAnalysis || targetLanguage === 'English') {
      // Reset to original English
      setOtherAnalysis(otherOriginalAnalysis);
      const emojiPattern = /🫀|🫁|🧠|💧|🔬|💊|⚠️|🚨|⏰|🔍|👨‍⚕️|🏥|😴|📊|🩹|🤢|🍽️|🚶‍♂️|👶|🍼|💉|👪/;
      const match = otherOriginalAnalysis.match(emojiPattern);
      const summaryText = match && match.index !== undefined ? otherOriginalAnalysis.substring(0, match.index).trim() : otherOriginalAnalysis;
      setOtherAnalysisSummary(summaryText);
      setEditedOtherSummary(summaryText);
      setOtherSelectedLanguage('English');
      return;
    }
    
    setIsOtherTranslating(true);
    setOtherSelectedLanguage(targetLanguage);
    
    try {
      const response = await axios.post<{ success: boolean; translated_text?: string }>(`${BACKEND_URL}/translate`, {
        text: otherOriginalAnalysis,
        target_language: targetLanguage,
        api_key: process.env.REACT_APP_OPENAI_API_KEY
      });
      
      if (response.data.success && response.data.translated_text) {
        const translatedText = response.data.translated_text;
        setOtherAnalysis(translatedText);
        
        // Extract summary from translated text
        const emojiPattern = /🫀|🫁|🧠|💧|🔬|💊|⚠️|🚨|⏰|🔍|👨‍⚕️|🏥|😴|📊|🩹|🤢|🍽️|🚶‍♂️|👶|🍼|💉|👪/;
        const match = translatedText.match(emojiPattern);
        const summaryText = match && match.index !== undefined ? translatedText.substring(0, match.index).trim() : translatedText;
        setOtherAnalysisSummary(summaryText);
        setEditedOtherSummary(summaryText);
      }
    } catch (error) {
      console.error("Translation error:", error);
      setStatusMsg("Translation failed. Please try again.");
    }
    setIsOtherTranslating(false);
  }, [otherOriginalAnalysis]);

  const submitOtherRecording = useCallback(async () => {
    if (!otherTranscriptionData || !patientData || !loggedInUser) {
      setOtherSubmitStatus("❌ Missing required data for submission.");
      return;
    }
    
    const transcript = otherTranscriptionData.translation || otherTranscriptionData.transcription;
    if (!transcript) {
      setOtherSubmitStatus("❌ No transcript available.");
      return;
    }
    
    setIsSubmittingOther(true);
    setOtherSubmitStatus("📤 Submitting recording...");
    
    // Map interaction type to readable name
    const interactionNames: { [key: string]: string } = {
      'clinical_interaction': 'Clinical Interaction',
      'family_conference': 'Family Conference',
      'care_plan_discussion': 'Care Plan Discussion',
      'education_session': 'Education Session'
    };
    const recordingType = interactionNames[selectedInteractionType] || selectedInteractionType;
    
    try {
      const response = await axios.post<{ success: boolean; message?: string }>(`${BACKEND_URL}/other_recordings/submit`, {
        nurse_name: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
        nurse_email: loggedInUser.email,
        patient_name: patientData.Name,
        mrn: patientData.MRN,
        original_language: otherTranscriptionData.language_name || 'English',
        recording_type: recordingType,
        transcript: transcript,
        summary: editedOtherSummary || otherAnalysisSummary
      });
      
      if (response.data.success) {
        setOtherSubmitStatus("✅ Recording submitted successfully!");
        // Increment other recordings counter
        setNurseStats(prev => ({
          ...prev,
          otherRecordingsSubmitted: prev.otherRecordingsSubmitted + 1
        }));
        // Reset the form after successful submission
        setTimeout(() => {
          setOtherAudioBlob(null);
          setOtherTranscriptionData(null);
          setOtherAnalysis('');
          setOtherAnalysisSummary('');
          setOtherOriginalAnalysis('');
          setEditedOtherSummary('');
          setSelectedInteractionType('');
          setOtherSelectedLanguage('English');
          setOtherSubmitStatus('');
        }, 2000);
      } else {
        setOtherSubmitStatus(`❌ ${response.data.message || 'Submission failed.'}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      setOtherSubmitStatus("❌ Error submitting recording. Please try again.");
    }
    setIsSubmittingOther(false);
  }, [otherTranscriptionData, patientData, loggedInUser, selectedInteractionType, editedOtherSummary, otherAnalysisSummary]);

  // Email nurse scribe recording (also submits to database)
  const emailOtherRecording = useCallback(async () => {
    if (!otherTranscriptionData || !patientData || !loggedInUser) {
      setOtherSubmitStatus("❌ Missing required data for email.");
      return;
    }
    
    const transcript = otherTranscriptionData.translation || otherTranscriptionData.transcription;
    if (!transcript) {
      setOtherSubmitStatus("❌ No transcript available.");
      return;
    }
    
    setIsSubmittingOther(true);
    setOtherSubmitStatus("📧 Sending email and submitting...");
    
    // Map interaction type to readable name
    const interactionNames: { [key: string]: string } = {
      'clinical_interaction': 'Clinical Interaction',
      'family_conference': 'Family Conference',
      'care_plan_discussion': 'Care Plan Discussion',
      'education_session': 'Education Session'
    };
    const recordingType = interactionNames[selectedInteractionType] || selectedInteractionType;
    const summary = editedOtherSummary || otherAnalysisSummary;
    
    try {
      // First submit to database
      const submitResponse = await axios.post<{ success: boolean; message?: string }>(`${BACKEND_URL}/other_recordings/submit`, {
        nurse_name: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
        nurse_email: loggedInUser.email,
        patient_name: patientData.Name,
        mrn: patientData.MRN,
        original_language: otherTranscriptionData.language_name || 'English',
        recording_type: recordingType,
        transcript: transcript,
        summary: summary
      });
      
      if (!submitResponse.data.success) {
        setOtherSubmitStatus(`❌ ${submitResponse.data.message || 'Submission failed.'}`);
        setIsSubmittingOther(false);
        return;
      }
      
      // Then send email
      const emailResponse = await axios.post<{ success: boolean; message?: string }>(`${BACKEND_URL}/other_recordings/email`, {
        nurse_email: loggedInUser.email,
        nurse_name: `${loggedInUser.first_name} ${loggedInUser.last_name}`,
        patient_name: patientData.Name,
        mrn: patientData.MRN,
        recording_type: recordingType,
        summary: summary,
        key_points: otherKeyPoints,
        categorical_breakdown: otherCategoricalBreakdown
      });
      
      if (emailResponse.data.success) {
        setOtherSubmitStatus("✅ Email sent and recording submitted!");
        // Increment counter
        setNurseStats(prev => ({
          ...prev,
          otherRecordingsSubmitted: prev.otherRecordingsSubmitted + 1
        }));
        // Reset the form after successful submission
        setTimeout(() => {
          setOtherAudioBlob(null);
          setOtherTranscriptionData(null);
          setOtherAnalysis('');
          setOtherAnalysisSummary('');
          setOtherOriginalAnalysis('');
          setEditedOtherSummary('');
          setSelectedInteractionType('');
          setOtherSelectedLanguage('English');
          setOtherSubmitStatus('');
        }, 2000);
      } else {
        setOtherSubmitStatus(`⚠️ Submitted but email failed: ${emailResponse.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Email error:", error);
      setOtherSubmitStatus("❌ Error sending email. Please try again.");
    }
    setIsSubmittingOther(false);
  }, [otherTranscriptionData, patientData, loggedInUser, selectedInteractionType, editedOtherSummary, otherAnalysisSummary, otherKeyPoints, otherCategoricalBreakdown]);

  // Print nurse scribe recording
  const printOtherRecording = useCallback(() => {
    window.print();
  }, []);

  // Fetch dynamic highlights from OpenAI
  const fetchDynamicHighlights = useCallback(async (text: string, department: string) => {
    try {
      const response = await axios.post<{
        success: boolean;
        highlights: {
          'critical-info': string[];
          'vital-sign': string[];
          'high-priority': string[];
          'medication-highlight': string[];
        };
      }>(`${BACKEND_URL}/handoff/extract-highlights`, {
        text,
        department
      });
      
      if (response.data.success && response.data.highlights) {
        setDynamicHighlights(response.data.highlights);
        return response.data.highlights;
      }
    } catch (error) {
      console.error('Error fetching dynamic highlights:', error);
    }
    return null;
  }, []);

  // Apply dynamic highlights to text
  const applyDynamicHighlights = useCallback((text: string, highlights: {
    'critical-info': string[];
    'vital-sign': string[];
    'high-priority': string[];
    'medication-highlight': string[];
  } | null): string => {
    if (!highlights) return text;
    
    // Process line by line to skip header lines
    const lines = text.split('\n');
    const processedLines = lines.map(line => {
      // Check if this is a category header line (starts with emoji and contains mostly uppercase)
      const headerPattern = /^[🫀🫁🧠💧🔬💊⚠️🚨⏰🔍👨‍⚕️🏥😴📊🩹🤢🍽️🚶‍♂️👶🍼💉👪🎗️🧪🤒🩺❤️🧒🍎👨‍👩‍👧🧑‍⚕️💭🏠🔄]\s*[A-Z\s&]+$/;
      if (headerPattern.test(line.trim())) {
        return line;
      }
      
      let highlightedLine = line;
      
      // Apply highlights for each category (longer phrases first to avoid partial matches)
      const categories: Array<'critical-info' | 'vital-sign' | 'high-priority' | 'medication-highlight'> = 
        ['critical-info', 'vital-sign', 'high-priority', 'medication-highlight'];
      
      categories.forEach(category => {
        const terms = highlights[category] || [];
        // Sort by length descending to match longer phrases first
        const sortedTerms = [...terms].sort((a, b) => b.length - a.length);
        
        sortedTerms.forEach(term => {
          if (term && term.length > 1) {
            // Escape special regex characters in the term
            const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Use word boundaries but be flexible for phrases
            const regex = new RegExp(`(?<!<span[^>]*>)\\b(${escapedTerm})\\b(?![^<]*<\\/span>)`, 'gi');
            highlightedLine = highlightedLine.replace(regex, `<span class='${category}'>$1</span>`);
          }
        });
      });
      
      return highlightedLine;
    });
    
    return processedLines.join('\n');
  }, []);

  const highlightKeywords = useCallback((text: string): string => {
    // If we have dynamic highlights, use them
    if (dynamicHighlights) {
      return applyDynamicHighlights(text, dynamicHighlights);
    }
    
    // Fallback to static keywords if dynamic highlights not available
    const keywords = {
      "(confused|confusion|agitated|delirium|fall risk|high fall risk|bed alarm|restraints|suicide risk)":
        "<span class='critical-info'>$1</span>",
      "(low blood pressure|high blood pressure|low bp|bradycardia|tachycardia|spo2|oxygen|abnormal electrolytes|cardiac enzymes|blood counts|glucose levels|blood pressure|bp|vital signs)":
        "<span class='vital-sign'>$1</span>",
      "(pain|pain level|discomfort|shortness of breath|chest pain|current pain score|PRN given|lab abnormality|pain score)":
        "<span class='high-priority'>$1</span>",
      "(medication changes|new orders|held medications|timing changes|adverse reactions|drug allergies|food allergies|latex|medication|medications|med|meds|dose|dosage|iv|lasix|morphine|insulin|antibiotic)":
        "<span class='medication-highlight'>$1</span>",
      "(I&O status|fluid restriction|catheter output|strict I&Os|fluid overload|isolation|contact precautions|droplet precautions|airborne precautions|C-diff|MRSA|intake|output)":
        "<span class='high-priority'>$1</span>",
      "(code status|full code|DNR|DNI|comfort care only|pending orders|labs due|scheduled procedure|physician call back|discharge planning|family concern|family issues|interpreter needed|emergency contact|stat|urgent|emergency)":
        "<span class='vital-sign'>$1</span>",
      "(insufficient|absence|ineffective|lack of|poor|interruption|unsuccessfully|inappropriate|inaccurate|incomplete|unclear|delay|refuse|refused|not done|never|not reported|not notify|not received|not given|not documented|not placed|not completed|no documentation|no orders|no communication|miscommunication|not communicated|not ordered|no IV|not transferred|not released|no sitter|not inform|not followed|not aware|discontinued|not consulted)":
        "<span class='critical-info'>$1</span>"
    };

    // Process line by line to skip header lines (lines with emojis followed by ALL CAPS text)
    const lines = text.split('\n');
    const processedLines = lines.map(line => {
      // Check if this is a category header line (starts with emoji and contains mostly uppercase)
      const headerPattern = /^[🫀🫁🧠💧🔬💊⚠️🚨⏰🔍👨‍⚕️🏥😴📊🩹🤢🍽️🚶‍♂️👶🍼💉👪]\s*[A-Z\s&]+$/;
      if (headerPattern.test(line.trim())) {
        // Don't highlight header lines
        return line;
      }
      
      // Apply highlighting to non-header lines
      let highlightedLine = line;
      Object.entries(keywords).forEach(([pattern, replace]) => {
        const regex = new RegExp(`\\b${pattern}\\b`, 'gi');
        highlightedLine = highlightedLine.replace(regex, replace);
      });
      return highlightedLine;
    });
    
    return processedLines.join('\n');
  }, [dynamicHighlights, applyDynamicHighlights]);
  
  const generateHandoff = useCallback(async () => {
    if (!patientData || !process.env.REACT_APP_OPENAI_API_KEY) {
      setStatusMsg("Patient data and OpenAI API key required for handoff generation");
      return;
    }

    // Scroll behavior: wide screens (2-column layout) scroll to top, narrow screens (1-column layout) scroll to bottom
    // The layout switches to single-column at 1024px in CSS, but we use 1100px here to ensure
    // iPad Pro portrait (1024px) and other tablets are properly detected as single-column
    const isSingleColumnLayout = window.innerWidth <= 1100;
    if (isSingleColumnLayout) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Set handoff type BEFORE API call so spinner shows correctly
    setHandoffType('Generated by CareShift');
    setOriginalLanguage('English');
    setAnalysis('');
    setEditedTranscript('');
    setProcessing(true);
    
    // Show department-specific agent message
    const department = patientData.Department || 'Med-Surg';
    setStatusMsg(`Contacting the ${department} Agent...`);
    
    // Get the logged-in user's name to filter activities
    const loggedByName = loggedInUser ? `${loggedInUser.first_name} ${loggedInUser.last_name}` : '';
    
    // Get the saved patient report data for this patient
    const savedReport = patientReports[patientData.MRN] || null;
    
    try {
      // Use LangGraph-based endpoint for handoff generation
      const response = await axios.post(`${BACKEND_URL}/agents/handoff/generate`, {
        patient_id: patientData.MRN,
        logged_by: loggedByName,
        patient_report: savedReport,
        department: department
      });

      const data = response.data as { 
        success: boolean; 
        handoff_text?: string; 
        highlighted_handoff?: string;
        message?: string 
      };

      console.log('Handoff API response:', data);
      
      if (data.success && data.handoff_text) {
        // Use raw text - highlighting will only be applied to categorical breakdown
        const rawText = data.handoff_text || "";
        console.log('Raw handoff text:', rawText);
        
        // Fetch dynamic highlights from OpenAI for department-specific highlighting
        setStatusMsg("Analyzing important clinical information...");
        const highlights = await fetchDynamicHighlights(rawText, department);
        
        // Extract the narrative portion (before the first emoji) for typing animation
        const narrativeText = extractCorrectedTranscript(rawText);
        console.log('Narrative text:', narrativeText);
        
        // Extract the categorical breakdown (from first emoji onwards)
        const emojiPattern = /🫀|🫁|🧠|💧|🔬|💊|⚠️|🚨|⏰|🔍|👨‍⚕️|🏥|😴|📊|🩹|🤢|🍽️|🚶‍♂️|👶|🍼|💉|👪|🎗️|🧪|🤒|🩺|❤️|🧒|🍎|👨‍👩‍👧|🧑‍⚕️|💭|🏠|🔄/;
        const match = rawText.match(emojiPattern);
        const categoricalBreakdown = match ? rawText.slice(match.index) : "";
        
        // Apply dynamic highlights if available, otherwise fall back to static
        const highlightedBreakdown = highlights 
          ? applyDynamicHighlights(categoricalBreakdown, highlights)
          : highlightKeywords(categoricalBreakdown);
        
        // Update status for typing animation
        setStatusMsg("Generating handoff narrative...");
        
        let currentIndex = 0;
        const typingSpeed = 12; // milliseconds per character
        
        const typeNextChar = () => {
          if (currentIndex < narrativeText.length) {
            const currentText = narrativeText.slice(0, currentIndex + 1);
            // Apply highlighting to narrative as it types (use dynamic if available)
            const highlightedCurrentText = highlights 
              ? applyDynamicHighlights(currentText, highlights)
              : highlightKeywords(currentText);
            setEditedTranscript(highlightedCurrentText);
            currentIndex++;
            setTimeout(typeNextChar, typingSpeed);
          } else {
            // Typing complete - set the full analysis (highlighted narrative + highlighted categorical breakdown)
            const highlightedNarrative = highlights 
              ? applyDynamicHighlights(narrativeText, highlights)
              : highlightKeywords(narrativeText);
            const fullHighlightedAnalysis = highlightedNarrative + (categoricalBreakdown ? "\n\n" + highlightedBreakdown : "");
            setAnalysis(fullHighlightedAnalysis);
            // Keep the highlighted narrative for display
            setEditedTranscript(highlightedNarrative);
            setStatusMsg("AI handoff generated successfully!");
          }
        };
        
        typeNextChar();
        // Note: Handoff counter is incremented when user submits the handoff via handleHandoffSubmitted
      } else {
        setStatusMsg(`Handoff generation failed: ${data.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Handoff generation error:", error);
      setStatusMsg("Error generating handoff. Please check your API key and try again.");
    }
    
    setProcessing(false);
  }, [patientData, loggedInUser, patientReports, fetchDynamicHighlights, applyDynamicHighlights, highlightKeywords]);

  // Static header text (removed typing animation)
  useEffect(() => {
    // Only run if authenticated and shift has started (main app is visible)
    if (!isAuthenticated || !hasStartedShift) return;
    
    const textElement = document.getElementById('text');
    if (!textElement) return;

    // Set static text immediately
    textElement.textContent = 'CareShift™';
  }, [isAuthenticated, hasStartedShift]);

  // Check if we're on the reset-password page - MUST be checked first before any auth checks
  const isResetPasswordPage = window.location.pathname === '/reset-password' || window.location.pathname === '/reset-password/';
  
  // Check if we're on the verify-email page - MUST be checked first before any auth checks
  const isVerifyEmailPage = window.location.pathname === '/verify-email' || window.location.pathname === '/verify-email/';
  
  // Debug: Log the current pathname to help diagnose routing issues
  console.log('Current pathname:', window.location.pathname, 'isVerifyEmailPage:', window.location.pathname === '/verify-email' || window.location.pathname === '/verify-email/');
  
  // Handle navigation back to login from reset password page
  const handleBackToLoginFromReset = () => {
    window.history.pushState({}, '', '/');
    window.location.reload();
  };
  
  // Handle navigation back to login from verify email page
  const handleVerificationComplete = () => {
    window.history.pushState({}, '', '/');
    window.location.reload();
  };
  
  // Show reset password page if on that route - before any other checks
  if (isResetPasswordPage) {
    return <ResetPassword onBackToLogin={handleBackToLoginFromReset} />;
  }
  
  // Show verify email page if on that route - before any other checks
  if (isVerifyEmailPage) {
    return <VerifyEmail onVerificationComplete={handleVerificationComplete} />;
  }

  // Show logout summary screen
  if (showLogoutSummary && logoutSummaryData) {
    return (
      <LogoutSummary
        userName={logoutSummaryData.userName}
        activitiesLogged={logoutSummaryData.activitiesLogged}
        handoffsSubmitted={logoutSummaryData.handoffsSubmitted}
        patientsCaresFor={logoutSummaryData.patientsCaresFor}
        sessionDuration={logoutSummaryData.sessionDuration}
        reportsSubmitted={logoutSummaryData.reportsSubmitted}
        otherRecordingsSubmitted={logoutSummaryData.otherRecordingsSubmitted}
        onContinueToLogin={completeLogout}
        isAdmin={logoutSummaryData.isAdmin}
        isNurseAdminDashboard={logoutSummaryData.isNurseAdminDashboard}
      />
    );
  }

  // ADD THIS: Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Check if user is admin (super admin)
  const isAdmin = loggedInUser?.email === ADMIN_EMAIL;
  
  // Check if user is a nurse admin
  const isNurseAdmin = loggedInUser?.is_nurse_admin === true;

  // Show nurse admin landing screen to choose between admin dashboard and nurse view
  if (isAuthenticated && showNurseAdminLanding && isNurseAdmin) {
    return (
      <div className="nurse-admin-landing">
        <div className="nurse-admin-background-dots" />
        <div className="nurse-admin-content">
          <div className="nurse-admin-logo-section">
            <div className="nurse-admin-brand-row">
              <img src="/logo.png" alt="CareShift Logo" className="nurse-admin-logo-image" />
              <h2 className="nurse-admin-brand-title">CareShift</h2>
            </div>
          </div>
          
          <div className="nurse-admin-text-section">
            <h1 className="nurse-admin-title">Welcome, {loggedInUser?.first_name}!</h1>
            <p className="nurse-admin-subtitle">Select how you'd like to proceed</p>
          </div>
          
          <div className="nurse-admin-options">
            <button 
              className="nurse-admin-option admin-dashboard-option"
              onClick={() => {
                setShowNurseAdminLanding(false);
                setShowAnalytics(true);
                setHasStartedShift(true);
                localStorage.setItem('nurseAdminOnDashboard', 'true');
              }}
            >
              <div className="option-icon">
                <BarChart3 size={32} />
              </div>
              <div className="option-content">
                <h2>Admin Dashboard</h2>
                <p>View analytics and manage your team</p>
              </div>
            </button>
            <button 
              className="nurse-admin-option nurse-view-option"
              onClick={() => {
                setShowNurseAdminLanding(false);
                localStorage.removeItem('nurseAdminOnDashboard');
              }}
            >
              <div className="option-icon">
                <Stethoscope size={32} />
              </div>
              <div className="option-content">
                <h2>Nurse View</h2>
                <p>Start your shift and record patient activities</p>
              </div>
            </button>
          </div>
          
          <button 
            className="nurse-admin-logout-btn"
            onClick={handleLogout}
          >
            <LogOut size={18} />
            <span>Log Out</span>
          </button>
        </div>
      </div>
    );
  }

  // Show analytics dashboard for admin users (super admin or nurse admin)
  if (isAuthenticated && showAnalytics && (isAdmin || isNurseAdmin)) {
    return (
      <AnalyticsDashboard 
        onLogout={handleLogout} 
        userName={loggedInUser ? `${loggedInUser.first_name} ${loggedInUser.last_name}` : 'Admin'}
        userEmail={loggedInUser?.email}
        isNurseAdmin={isNurseAdmin}
      />
    );
  }

  // ADD THIS: Show welcome page if authenticated but hasn't started shift
  if (isAuthenticated && !hasStartedShift) {
    return (
      <WelcomePage 
        onStartShift={handleStartShift} 
        userName={loggedInUser ? `${loggedInUser.first_name} ${loggedInUser.last_name}` : 'User'}
        isAdmin={isAdmin}
        onViewAnalytics={() => setShowAnalytics(true)}
      />
    );
  }

  // EXISTING: Main app content (only shows when authenticated)
  return (
    <div className="app-root">
      <SessionTimeout 
        onLogout={handleSessionTimeoutLogout}
        timeoutMinutes={5}
        warningSeconds={30}
        isAuthenticated={isAuthenticated && hasStartedShift}
        isPaused={isRecording || isOtherRecording}
      />
      <LogoutWarningModal
        isOpen={showLogoutWarning}
        onLogoutAnyway={handleLogoutAnyway}
        onStayLoggedIn={handleStayLoggedIn}
      />
      <ReportUpdateToast
        messages={toastMessages}
        onDismiss={dismissToast}
      />
      <button 
        className={`sidebar-toggle ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        aria-label="Toggle Sidebar"
      >
        {sidebarCollapsed ? '›' : '‹'}
      </button>

      <div 
        className={`sidebar-overlay ${!sidebarCollapsed ? 'active' : ''}`}
        onClick={() => setSidebarCollapsed(true)}
      />
      
      <Sidebar 
        className={sidebarCollapsed ? 'collapsed' : ''}
        patientMRN={patientMRN}
        setPatientMRN={setPatientMRN}
        claudeApiKey={claudeApiKey}
        backendAvailable={backendAvailable}
        lookupPatient={lookupPatient}
        samplePatients={samplePatients}
        setSamplePatients={setSamplePatients}
        resetSession={resetSession}
        loggedInUserName={loggedInUser ? `${loggedInUser.first_name} ${loggedInUser.last_name}` : ''}
        loggedInUserEmail={loggedInUser?.email || ''}
        onLogout={handleLogout}
        shiftStartTime={shiftStartTime}
        shiftDuration={shiftDuration}
        nurseStats={nurseStats}
      />

      <main className={sidebarCollapsed ? 'sidebar-collapsed' : ''}>
        <div className="main-header">
          <div className="title-container">
            <img src="logo.png" alt="CareShift Logo" className="logo" />
            <h1 className="typewriter">
              <span className="typewriter-text" id="text"></span>
            </h1>
          </div>
          <h3>
            Intelligent Nurse Handoff Assistant with Multi-Language Support
          </h3>
          <p>Welcome To Your Shift!</p>
        </div>
        
        {/* Tab Navigation - Below header, left-aligned */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'handoffs' ? 'active' : ''}`}
            onClick={() => setActiveTab('handoffs')}
          >
            Handoffs
          </button>
          <button
            className={`tab-button ${activeTab === 'report' ? 'active' : ''}`}
            onClick={() => setActiveTab('report')}
          >
            Shift Report
          </button>
          <button
            className={`tab-button ${activeTab === 'other' ? 'active' : ''}`}
            onClick={() => setActiveTab('other')}
          >
            Nurse Scribe
          </button>
        </div>
        
        {!backendAvailable && (
          <div className="section-card" style={{ backgroundColor: "#ffebee", borderLeft: "4px solid #f44336" }}>
            <b>⚠️ Backend Connection Error:</b> Backend is not available. Please ensure the backend server is running on port 8000.
          </div>
        )}
        
        {!claudeApiKey && (
          <div className="section-card" style={{ backgroundColor: "#fff3e0", borderLeft: "4px solid #ff9800" }}>
            <b>⚠️ Configuration Error:</b> Claude API key not found. Please set REACT_APP_CLAUDE_API_KEY in your environment variables.
          </div>
        )}

        {/* Patient Report Tab Content */}
        {activeTab === 'report' && (
          <PatientReport
            patientData={patientData}
            savedReport={patientData?.MRN ? patientReports[patientData.MRN] : undefined}
            onSaveReport={(mrn, report) => {
              setPatientReports(prev => ({
                ...prev,
                [mrn]: report
              }));
            }}
            loggedInUser={loggedInUser}
            onReportSubmitted={() => {
              setNurseStats(prev => ({
                ...prev,
                reportsSubmitted: prev.reportsSubmitted + 1
              }));
            }}
          />
        )}

        {activeTab === 'handoffs' && (
        <div className="content-columns">
          <section className="column-left">
            <PatientCard patientData={patientData} />
            
            {!patientData && (
              <div className="empty-state-container">
                <div className="empty-state-icon">
                  <Users size={80} strokeWidth={1.5} />
                </div>
                <h3 className="empty-state-title">Select a Patient to Get Started</h3>
                <p className="empty-state-text">
                  Choose a patient from the sidebar or add a new one to begin recording activities and generating handoffs.
                </p>
                <div className="empty-state-hint">
                  <ArrowLeft size={16} />
                  <span>Use the patient list in the sidebar</span>
                </div>
              </div>
            )}
            
            {patientData && (
              <>
                {/* Key Focus Areas - Analyzes previous handoff for action items (Rehab only) */}
                <KeyFocusAreas
                  patientMRN={patientData.MRN}
                  patientName={patientData.Name}
                  department={patientData.Department || ''}
                  currentNurseId={loggedInUser?.email || ''}
                  cachedFocusAreas={focusAreasCache}
                  onCacheUpdate={(mrn, data) => setFocusAreasCache(prev => ({ ...prev, [mrn]: data }))}
                />

                {/* Previous Handoff - Shows if a previous nurse submitted a handoff */}
                <PreviousHandoff 
                  patientMRN={patientData.MRN}
                  currentNurseId={loggedInUser?.email || ''}
                  department={patientData.Department}
                />

                {/* Patient Activity Panel - Shows when panel is visible */}
                {showActivityPanel && (
                  <PatientActivityPanel 
                    patientMRN={patientData.MRN} 
                    patientName={patientData.Name}
                    onBack={() => {
                      setShowActivityPanel(false);
                      resetSession();
                    }}
                    loggedInUserName={loggedInUser ? `${loggedInUser.first_name} ${loggedInUser.last_name}` : ''}
                    onActivitySaved={handleActivitySavedWithSync}
                  />
                )}

                {/* End Shift Button - Shows when activity panel is visible and shift not ended */}
                {showActivityPanel && !shiftEnded && (
                  <div className="end-shift-container">
                    <button 
                      className="end-shift-btn"
                      onClick={() => setShiftEnded(true)}
                    >
                      End Shift
                    </button>
                    <p className="end-shift-hint">Tap to finalize activities and generate handoffs</p>
                  </div>
                )}

                {/* Shift-level controls - Only show after End Shift is tapped */}
                {shiftEnded && (
                  <>
                    <div className="shift-ended-banner">
                      <button 
                        className="undo-end-shift-btn"
                        onClick={() => setShiftEnded(false)}
                        title="Undo End Shift"
                      >
                        ↩ Undo
                      </button>
                      <span>Shift Ended</span>
                      <p>Record final wrap-up or generate handoffs below</p>
                    </div>
                    
                    <AudioControls
                      isRecording={isRecording}
                      recordStartTime={recordStartTime}
                      audioBlob={audioBlob}
                      audioProcessed={audioProcessed}
                      processing={processing}
                      backendAvailable={backendAvailable}
                      statusMsg={statusMsg}
                      claudeApiKey={claudeApiKey}
                      patientData={patientData}
                      transcriptionData={transcriptionData}
                      analysis={analysis}
                      startRecording={startRecording}
                      stopRecording={stopRecording}
                      transcribeAudio={transcribeAudio}
                      generateHandoff={generateHandoff}
                    />

                    <TranscriptionDisplay 
                      transcriptionData={transcriptionData}
                      onAnalyze={() => {
                        if (transcriptionData && patientData) {
                          // Custom smooth scroll to top using requestAnimationFrame
                          const scrollToTop = () => {
                            const currentPosition = window.pageYOffset || document.documentElement.scrollTop;
                            if (currentPosition > 0) {
                              const scrollStep = Math.max(currentPosition / 8, 10);
                              window.scrollTo(0, currentPosition - scrollStep);
                              requestAnimationFrame(scrollToTop);
                            }
                          };
                          requestAnimationFrame(scrollToTop);
                          
                          // Clear the edited transcript so original doesn't show during analysis
                          setEditedTranscript("");
                          
                          const transcript = transcriptionData.translation ?? transcriptionData.transcription;
                          setHandoffType('Recorded Audio');
                          setOriginalLanguage(transcriptionData.language_name || 'English');
                          analyzeWithClaude(transcript, patientData, claudeApiKey);
                        }
                      }}
                      processing={processing}
                      patientData={patientData}
                      analysis={analysis}
                    />
                  </>
                )}
              </>
            )}
          </section>

          <section className="column-right" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <AnalysisSection
              claudeApiKey={claudeApiKey}
              transcriptionData={transcriptionData}
              analysis={analysis}
              processing={processing}
              patientData={patientData}
              editedTranscript={editedTranscript}
              setEditedTranscript={setEditedTranscript}
              statusMsg={statusMsg}
              setStatusMsg={setStatusMsg}
              nurseName={loggedInUser ? `${loggedInUser.first_name} ${loggedInUser.last_name}` : ''}
              employeeId={loggedInUser?.email || ''}
              analyzeWithClaude={analyzeWithClaude}
              highlightKeywords={highlightKeywords}
              setAnalysis={setAnalysis}
              onHandoffSubmitted={handleHandoffSubmitted}
              handoffType={handoffType}
              originalLanguage={originalLanguage}
            />
          </section>
        </div>
        )}
        
        {/* Record Other Tab Content */}
        {activeTab === 'other' && (
          <div className="content-columns">
            <section className="column-left">
              <PatientCard patientData={patientData} />
              
              {!patientData && (
                <div className="empty-state-container">
                  <div className="empty-state-icon">
                    <Users size={80} strokeWidth={1.5} />
                  </div>
                  <h3 className="empty-state-title">Select a Patient to Get Started</h3>
                  <p className="empty-state-text">
                    Choose a patient from the sidebar or add a new one to begin recording activities and generating handoffs.
                  </p>
                  <div className="empty-state-hint">
                    <ArrowLeft size={16} />
                    <span>Use the patient list in the sidebar</span>
                  </div>
                </div>
              )}
              
              {patientData && (
                <div className="record-other-content">
                  {/* Previous Recordings from other nurses */}
                  <PreviousRecordings
                    patientMRN={patientData.MRN}
                    currentNurseEmail={loggedInUser?.email || ''}
                  />
                  
                  {/* Content for Record Other tab when patient is selected */}
                  <div className="section-card">
                    <div className="interaction-type-row">
                      <label className="interaction-type-label">What type of interaction will you be recording? [Limit 109 minutes]</label>
                      <TooltipSelect
                        value={selectedInteractionType}
                        onChange={(value) => setSelectedInteractionType(value)}
                        placeholder="Select"
                        options={[
                          {
                            value: 'clinical_interaction',
                            label: 'Clinical Interaction',
                            tooltip: 'For routine patient interactions such as intake, follow-ups, consults, and bedside conversations.'
                          },
                          {
                            value: 'familiy_conference',
                            label: 'Family Conference',
                            tooltip: 'For structured discussions with family or caregivers about diagnosis, prognosis, or next steps.'
                          },
                          {
                            value: 'care_plan_discussion',
                            label: 'Care Plan Discussion',
                            tooltip: 'For conversations focused on goals, treatment plans, discharge planning, and coordination of care.'
                          },
                          {
                            value: 'education_session',
                            label: 'Education Session',
                            tooltip: 'For patient or family teaching, including medications, procedures, discharge instructions, or self-care.'
                          }
                        ]}
                      />
                    </div>
                    
                    {selectedInteractionType && !otherAudioBlob && (
                      <div className="button-row" style={{ marginTop: '1.5rem' }}>
                        <button 
                          className="record-btn"
                          onClick={startOtherRecording}
                          disabled={isOtherRecording}
                        >
                          Record
                        </button>
                      </div>
                    )}
                    
                    {/* Audio player after recording */}
                    {otherAudioBlob && (
                      <div className="audio-section" style={{ marginTop: '1.5rem' }}>
                        <h4>Recorded Audio</h4>
                        <audio 
                          controls 
                          src={URL.createObjectURL(otherAudioBlob)} 
                          style={{ width: '100%' }}
                        />
                        {!otherTranscriptionData && (
                          <div className="input-actions" style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'center', alignItems: 'center' }}>
                            <button
                              className="nurse-scribe-btn cancel-btn"
                              onClick={cancelNurseScribe}
                              style={{ 
                                padding: '0.75rem 1.5rem',
                                height: '44px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: 500,
                                fontSize: '0.95rem'
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="nurse-scribe-btn"
                              onClick={resetNurseScribeForRerecord}
                              style={{ 
                                padding: '0.75rem 1.5rem',
                                height: '44px',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                                color: 'white',
                                cursor: 'pointer',
                                fontWeight: 500,
                                fontSize: '0.95rem'
                              }}
                            >
                              Re-record
                            </button>
                            <button
                              className="nurse-scribe-btn"
                              onClick={() => transcribeOtherAudio(otherAudioBlob)}
                              disabled={isOtherTranscribing || !backendAvailable}
                              style={{ 
                                padding: '0.75rem 1.5rem',
                                height: '44px',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                color: 'white',
                                cursor: 'pointer',
                                fontWeight: 500,
                                fontSize: '0.95rem'
                              }}
                            >
                              {isOtherTranscribing ? (
                                <>
                                  <span className="spinner" style={{ 
                                    display: 'inline-block',
                                    width: '14px',
                                    height: '14px',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                    borderTop: '2px solid white',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginRight: '8px',
                                    verticalAlign: 'middle'
                                  }} />
                                  Transcribing...
                                </>
                              ) : 'Transcribe Audio'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Transcription display */}
                    {otherTranscriptionData && (
                      <div className="transcription-section" style={{ marginTop: '1.5rem' }}>
                        <h4>Transcription {otherTranscriptionData.language_name && otherTranscriptionData.language_name !== 'English' && `(Translated from ${otherTranscriptionData.language_name})`}</h4>
                        <div className="transcription-text" style={{ 
                          padding: '1rem', 
                          background: 'var(--neutral-100)', 
                          borderRadius: '8px',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {otherTranscriptionData.translation || otherTranscriptionData.transcription}
                        </div>
                        
                        {/* Action buttons row */}
                        <div className="input-actions" style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'center', alignItems: 'center' }}>
                          <button
                            className="nurse-scribe-btn cancel-btn"
                            onClick={cancelNurseScribe}
                            style={{ 
                              padding: '0.75rem 1.5rem',
                              height: '44px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 500,
                              fontSize: '0.95rem'
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="nurse-scribe-btn"
                            onClick={resetNurseScribeForRerecord}
                            style={{ 
                              padding: '0.75rem 1.5rem',
                              height: '44px',
                              borderRadius: '8px',
                              border: 'none',
                              background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                              color: 'white',
                              cursor: 'pointer',
                              fontWeight: 500,
                              fontSize: '0.95rem'
                            }}
                          >
                            Re-record
                          </button>
                          {!otherAnalysis && (
                            <button
                              className="nurse-scribe-btn"
                              onClick={analyzeOtherRecording}
                              disabled={isOtherAnalyzing || !backendAvailable}
                              style={{ 
                                padding: '0.75rem 1.5rem',
                                height: '44px',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                                color: 'white',
                                cursor: 'pointer',
                                fontWeight: 500,
                                fontSize: '0.95rem'
                              }}
                            >
                              {isOtherAnalyzing ? 'Analyzing...' : 'Analyze Recording'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    
                  </div>
                </div>
              )}
              
              {/* Recording Screen Overlay for Record Other tab */}
              {showOtherRecordingScreen && isOtherRecording && (
                <RecordingScreen onStop={stopOtherRecording} />
              )}
            </section>
            <section className="column-right">
              {/* Loading dialog while analyzing (before typing starts) - separate container */}
              {isOtherAnalyzing && !otherAnalysisSummary && (
                <div className="analysis-section section-card">
                  <div className="loading-dialog" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '3rem',
                    gap: '1rem'
                  }}>
                    <div className="spinner" style={{
                      width: '40px',
                      height: '40px'
                    }} />
                    <p style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>{analysisStep || 'Analyzing recording...'}</p>
                  </div>
                </div>
              )}
              
              {/* Analysis display in right column - only show when there's content */}
              {otherAnalysisSummary && (
                <div className="analysis-section section-card">
                  {/* Language selector */}
                  {otherAnalysis && !isOtherAnalyzing && (
                    <div className="language-selector-row" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <label className="language-label">Display in:</label>
                      <select
                        className="language-select"
                        value={otherSelectedLanguage}
                        onChange={(e) => translateOtherAnalysis(e.target.value)}
                        disabled={isOtherTranslating}
                      >
                        <option value="English">English</option>
                        <option value="Spanish">Spanish</option>
                        <option value="French">French</option>
                        <option value="German">German</option>
                        <option value="Hindi">Hindi</option>
                        <option value="Chinese">Chinese</option>
                        <option value="Japanese">Japanese</option>
                        <option value="Korean">Korean</option>
                        <option value="Portuguese">Portuguese</option>
                        <option value="Italian">Italian</option>
                        <option value="Russian">Russian</option>
                        <option value="Arabic">Arabic</option>
                        <option value="Dutch">Dutch</option>
                        <option value="Polish">Polish</option>
                        <option value="Turkish">Turkish</option>
                        <option value="Swedish">Swedish</option>
                        <option value="Danish">Danish</option>
                        <option value="Norwegian">Norwegian</option>
                        <option value="Finnish">Finnish</option>
                      </select>
                      {isOtherTranslating && <span className="translating-spinner">Translating...</span>}
                    </div>
                  )}
                  
                  {/* Collapsible Summary Section */}
                  <div className="collapsible-section" style={{ marginBottom: '1rem' }}>
                    <div 
                      className="collapsible-header"
                      onClick={() => setCollapsedSections(prev => ({ ...prev, summary: !prev.summary }))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.75rem 1rem',
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: collapsedSections.summary ? '8px' : '8px 8px 0 0',
                        cursor: 'pointer',
                        fontWeight: 600,
                        color: 'var(--text-primary)'
                      }}
                    >
                      <span>Summary</span>
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ 
                          transform: collapsedSections.summary ? 'rotate(0deg)' : 'rotate(180deg)', 
                          transition: 'transform 0.2s',
                          opacity: 0.7
                        }}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                    {!collapsedSections.summary && (
                      <div style={{ padding: '1rem', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 8px 8px', background: 'var(--card-bg)' }}>
                        {isEditingOtherSummary ? (
                          <div>
                            <textarea
                              className="editable-transcript"
                              value={editedOtherSummary}
                              onChange={(e) => setEditedOtherSummary(e.target.value)}
                              style={{ width: '100%', minHeight: '150px', marginBottom: '0.5rem' }}
                            />
                            <button
                              className="action-btn"
                              onClick={() => {
                                setOtherAnalysisSummary(editedOtherSummary);
                                setIsEditingOtherSummary(false);
                              }}
                              style={{ marginRight: '0.5rem' }}
                            >
                              Save
                            </button>
                            <button
                              className="action-btn"
                              onClick={() => {
                                setEditedOtherSummary(otherAnalysisSummary);
                                setIsEditingOtherSummary(false);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div>
                            <div 
                              className="analysis-text"
                              style={{ whiteSpace: 'pre-wrap', marginBottom: '0.5rem' }}
                              dangerouslySetInnerHTML={{ __html: highlightKeywords(otherAnalysisSummary) }}
                            />
                            {otherAnalysis && !isOtherAnalyzing && (
                              <button
                                className="action-btn"
                                onClick={() => setIsEditingOtherSummary(true)}
                                style={{ fontSize: '0.85rem' }}
                              >
                                Edit Summary
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Collapsible Key Points Section */}
                  {otherAnalysis && !isOtherAnalyzing && otherKeyPoints.length > 0 && (
                    <div className="collapsible-section" style={{ marginBottom: '1rem' }}>
                      <div 
                        className="collapsible-header"
                        onClick={() => setCollapsedSections(prev => ({ ...prev, keyPoints: !prev.keyPoints }))}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem 1rem',
                          background: 'var(--card-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: collapsedSections.keyPoints ? '8px' : '8px 8px 0 0',
                          cursor: 'pointer',
                          fontWeight: 600,
                          color: 'var(--text-primary)'
                        }}
                      >
                        <span>Key Points ({otherKeyPoints.length})</span>
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          style={{ 
                            transform: collapsedSections.keyPoints ? 'rotate(0deg)' : 'rotate(180deg)', 
                            transition: 'transform 0.2s',
                            opacity: 0.7
                          }}
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                      {!collapsedSections.keyPoints && (
                        <div style={{ padding: '1rem', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 8px 8px', background: 'var(--card-bg)' }}>
                          <ol style={{ margin: 0, paddingLeft: '1.5rem' }}>
                            {otherKeyPoints.map((point, index) => (
                              <li 
                                key={index} 
                                style={{ marginBottom: '0.5rem', lineHeight: 1.5 }}
                                dangerouslySetInnerHTML={{ __html: point }}
                              />
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Collapsible Categorical Breakdown Section */}
                  {otherAnalysis && !isOtherAnalyzing && Object.keys(otherCategoricalBreakdown).length > 0 && (
                    <div className="collapsible-section" style={{ marginBottom: '1rem' }}>
                      <div 
                        className="collapsible-header"
                        onClick={() => setCollapsedSections(prev => ({ ...prev, categorical: !prev.categorical }))}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem 1rem',
                          background: 'var(--card-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: collapsedSections.categorical ? '8px' : '8px 8px 0 0',
                          cursor: 'pointer',
                          fontWeight: 600,
                          color: 'var(--text-primary)'
                        }}
                      >
                        <span>Categorical Breakdown ({Object.keys(otherCategoricalBreakdown).length})</span>
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          style={{ 
                            transform: collapsedSections.categorical ? 'rotate(0deg)' : 'rotate(180deg)', 
                            transition: 'transform 0.2s',
                            opacity: 0.7
                          }}
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                      {!collapsedSections.categorical && (
                        <div style={{ padding: '1rem', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 8px 8px', background: 'var(--card-bg)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {Object.entries(otherCategoricalBreakdown).map(([category, content], index) => {
                            // Color mapping based on emoji or category index
                            const categoryColors: Record<string, string> = {
                              '🫀': '#e74c3c', '🫁': '#3498db', '🧠': '#9b59b6', '💧': '#1abc9c',
                              '🔬': '#f39c12', '💊': '#e67e22', '⚠️': '#f1c40f', '🚨': '#e74c3c',
                              '⏰': '#16a085', '🔍': '#2980b9', '👨‍⚕️': '#8e44ad', '🏥': '#27ae60',
                              '😴': '#6c5ce7', '📊': '#fd79a8', '🩹': '#a29bfe', '🤢': '#fdcb6e',
                              '🍽️': '#e17055', '🚶‍♂️': '#00b894', '👶': '#ff7675', '🍼': '#74b9ff',
                              '💉': '#fd79a8', '👪': '#55a3ff', '🎗️': '#ff69b4', '🧪': '#20c997',
                              '🤒': '#fd7e14', '🩺': '#17a2b8', '❤️': '#dc3545', '📈': '#28a745',
                              '👀': '#6f42c1', '🤰': '#e83e8c', '🩸': '#c0392b', '🗣️': '#3498db',
                              '🧩': '#9b59b6', '👥': '#2ecc71', '🦾': '#e67e22', '🏋️': '#27ae60',
                              '🛁': '#16a085', '📋': '#3498db', '🧸': '#f39c12', '🍎': '#e74c3c'
                            };
                            
                            // Fallback colors for categories without emojis
                            const fallbackColors = ['#3498db', '#e74c3c', '#2ecc71', '#9b59b6', '#f39c12', '#1abc9c', '#e67e22', '#6c5ce7'];
                            // Find color by checking if category starts with any known emoji
                            const foundEmoji = Object.keys(categoryColors).find(e => category.startsWith(e));
                            const borderColor = foundEmoji ? categoryColors[foundEmoji] : fallbackColors[index % fallbackColors.length];
                            
                            return (
                              <div 
                                key={index} 
                                className="category-section"
                                style={{ 
                                  padding: '1rem',
                                  borderLeft: `4px solid ${borderColor}`,
                                  borderRadius: '8px',
                                  background: 'var(--bg-secondary)',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}
                              >
                                <h4 style={{ 
                                  color: borderColor, 
                                  fontSize: '1rem', 
                                  fontWeight: 600, 
                                  marginBottom: '0.5rem',
                                  margin: 0
                                }}>
                                  {category}
                                </h4>
                                <div 
                                  style={{ 
                                    color: 'var(--text-primary)', 
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap'
                                  }}
                                  dangerouslySetInnerHTML={{ __html: content }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Action buttons: Submit, Email, Print */}
                  {otherAnalysis && !isOtherAnalyzing && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <button
                          className="generate-handoff-btn"
                          onClick={submitOtherRecording}
                          disabled={isSubmittingOther}
                          style={{ flex: '1', minWidth: '140px' }}
                        >
                          {isSubmittingOther && otherSubmitStatus.includes('📤') ? 'Submitting...' : 'Submit'}
                        </button>
                        <button
                          className="generate-handoff-btn"
                          onClick={emailOtherRecording}
                          disabled={isSubmittingOther}
                          style={{ 
                            flex: '1', 
                            minWidth: '140px',
                            background: 'linear-gradient(135deg, #10b981, #059669)'
                          }}
                        >
                          {isSubmittingOther && otherSubmitStatus.includes('📧') ? 'Sending...' : 'Email to Me'}
                        </button>
                        <button
                          className="generate-handoff-btn"
                          onClick={printOtherRecording}
                          disabled={isSubmittingOther}
                          style={{ 
                            flex: '1', 
                            minWidth: '140px',
                            background: 'linear-gradient(135deg, #6366f1, #4f46e5)'
                          }}
                        >
                          Print View
                        </button>
                      </div>
                      {otherSubmitStatus && (
                        <div 
                          className="submit-status"
                          style={{ 
                            marginTop: '0.75rem', 
                            padding: '0.75rem', 
                            borderRadius: '8px',
                            background: otherSubmitStatus.includes('✅') ? 'rgba(34, 197, 94, 0.1)' : 
                                        otherSubmitStatus.includes('❌') ? 'rgba(239, 68, 68, 0.1)' : 
                                        otherSubmitStatus.includes('⚠️') ? 'rgba(245, 158, 11, 0.1)' :
                                        'rgba(59, 130, 246, 0.1)',
                            color: otherSubmitStatus.includes('✅') ? '#22c55e' : 
                                   otherSubmitStatus.includes('❌') ? '#ef4444' : 
                                   otherSubmitStatus.includes('⚠️') ? '#f59e0b' :
                                   '#3b82f6'
                          }}
                        >
                          {otherSubmitStatus}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
        
        <footer className="footer-premium">
          <h4>CareShift - Transforming Healthcare Communication</h4>
        </footer>
      </main>
      
      {/* Feedback Widget - show for all users except super admin */}
      {loggedInUser && loggedInUser.email?.toLowerCase() !== ADMIN_EMAIL.toLowerCase() && (
        <FeedbackWidget
          userEmail={loggedInUser.email}
          isNurseAdmin={loggedInUser.is_nurse_admin || false}
          selectedDepartment={patientData?.Department || ''}
          currentPage={
            showAnalytics ? 'dashboard' :
            !hasStartedShift ? 'welcome' :
            activeTab === 'report' ? 'patient_report' :
            activeTab === 'handoffs' ? 'handoffs' :
            activeTab === 'other' ? 'clinical_interactions' :
            'other'
          }
        />
      )}
    </div>
  );
};

// Main App with Theme Provider
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;